import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Img from './Img';
import useScrollLock from '../hooks/useScrollLock';

const pad = (n, width) => String(n).padStart(width, '0');

// Throws if the pointer was already released (e.g. a cancelled touch).
export const capture = (e) => {
  try {
    e.currentTarget.setPointerCapture(e.pointerId);
  } catch {
    // Nothing to capture — the gesture still works from move events.
  }
};

/**
 * Full-screen photo viewer. Images are shown whole (contained), with an
 * aspect-true filmstrip. Touch: drag sideways to change photo, drag down
 * to close, tap to hide the chrome. Mouse/keyboard: crossfade, ← → Esc,
 * click the left/right half of the stage to step.
 */
const Viewer = ({ images, index, onIndex, onClose, title, altFor }) => {
  const [mode, setMode] = useState('fade');
  const [drag, setDrag] = useState({ x: 0, y: 0, active: false });
  const [chrome, setChrome] = useState(true);
  const gesture = useRef(null);
  const dialogRef = useRef(null);
  const stripRef = useRef(null);

  useScrollLock(true);

  const count = images.length;
  const width = String(count).length < 2 ? 2 : String(count).length;

  const go = useCallback(
    (delta, nextMode = 'fade') => {
      const next = index + delta;
      if (next < 0 || next >= count) return;
      setMode(nextMode);
      onIndex(next);
    },
    [index, count, onIndex]
  );

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'Home') onIndex(0);
      else if (e.key === 'End') onIndex(count - 1);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose, onIndex, count]);

  // Keep the active thumbnail centred in the filmstrip.
  useLayoutEffect(() => {
    const strip = stripRef.current;
    const thumb = strip?.querySelector('[data-active]');
    if (!strip || !thumb) return;
    const left = thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
    strip.scrollTo({ left, behavior: strip.dataset.ready ? 'smooth' : 'auto' });
    strip.dataset.ready = '1';
  }, [index]);

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    gesture.current = { id: e.pointerId, x: e.clientX, y: e.clientY, t: e.timeStamp, axis: null, touch: e.pointerType !== 'mouse' };
  };

  const onPointerMove = (e) => {
    const g = gesture.current;
    if (!g || g.id !== e.pointerId || !g.touch) return;
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;
    if (!g.axis) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      g.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      capture(e);
      setMode('slide');
    }
    if (g.axis === 'x') {
      const atEdge = (dx > 0 && index === 0) || (dx < 0 && index === count - 1);
      setDrag({ x: atEdge ? dx * 0.3 : dx, y: 0, active: true });
    } else {
      setDrag({ x: 0, y: Math.max(0, dy), active: true });
    }
  };

  const onPointerUp = (e) => {
    const g = gesture.current;
    gesture.current = null;
    if (!g || g.id !== e.pointerId) return;
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;
    const dt = Math.max(1, e.timeStamp - g.t);

    if (!g.axis) {
      // A tap / click.
      if (g.touch) setChrome((c) => !c);
      else {
        const rect = e.currentTarget.getBoundingClientRect();
        go(e.clientX - rect.left > rect.width / 2 ? 1 : -1);
      }
      return;
    }

    setDrag({ x: 0, y: 0, active: false });
    if (g.axis === 'y') {
      if (dy > 110 || dy / dt > 0.6) onClose();
      return;
    }
    const fling = Math.abs(dx) / dt > 0.35;
    if (Math.abs(dx) > window.innerWidth * 0.18 || (fling && Math.abs(dx) > 30)) go(dx < 0 ? 1 : -1, 'slide');
  };

  const onPointerCancel = () => {
    gesture.current = null;
    setDrag({ x: 0, y: 0, active: false });
  };

  const current = images[index];
  const frames = [index - 1, index, index + 1].filter((i) => i >= 0 && i < count);
  const dismiss = Math.min(1, drag.y / 400);

  return createPortal(
    <div
      ref={dialogRef}
      className={`viewer${chrome ? '' : ' viewer--bare'}`}
      role="dialog"
      aria-modal="true"
      aria-label={`${title} — photo viewer`}
      tabIndex={-1}
      style={{ '--dismiss': dismiss }}
    >
      <div className="viewer__bar">
        <span className="viewer__title">{title}</span>
        <span className="viewer__counter" aria-live="polite">
          {pad(index + 1, width)} <span aria-hidden="true">/</span> {pad(count, width)}
        </span>
        <button type="button" className="viewer__close" onClick={onClose} aria-label="Close viewer">
          <span />
        </button>
      </div>

      <div
        className="viewer__stage"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        style={{ transform: drag.y ? `translate3d(0, ${drag.y}px, 0) scale(${1 - dismiss * 0.12})` : undefined }}
      >
        {frames.map((i) => {
          const offset = i - index;
          const style =
            mode === 'slide'
              ? {
                  transform: `translate3d(calc(${offset * 100}% + ${offset * 24}px + ${drag.x}px), 0, 0)`,
                  transition: drag.active ? 'none' : undefined,
                }
              : { opacity: offset === 0 ? 1 : 0 };
          return (
            <div
              key={images[i].src}
              className={`viewer__frame viewer__frame--${mode}`}
              style={style}
              aria-hidden={offset !== 0}
            >
              <Img
                image={images[i]}
                alt={altFor(images[i], i)}
                fit="contain"
                hiRes
                eager
                sizes="100vw"
                style={{ '--a': images[i].aspect }}
              />
            </div>
          );
        })}
      </div>

      <div className="viewer__foot">
        <p className="viewer__caption">{current.caption}</p>
        <div className="viewer__filmstrip">
          <button
            type="button"
            className="viewer__arrow"
            onClick={() => go(-1)}
            aria-label="Previous photo"
            aria-disabled={index === 0}
          >
            ‹
          </button>
          <div className="viewer__thumbs" ref={stripRef}>
            {images.map((image, i) => (
              <button
                type="button"
                key={image.src}
                className="viewer__thumb"
                style={{ '--a': image.aspect }}
                data-active={i === index ? '' : undefined}
                onClick={() => onIndex(i)}
                aria-label={`Photo ${i + 1}`}
                aria-current={i === index ? 'true' : undefined}
              >
                <Img image={image} alt="" sizes="120px" />
              </button>
            ))}
          </div>
          <button
            type="button"
            className="viewer__arrow"
            onClick={() => go(1)}
            aria-label="Next photo"
            aria-disabled={index === count - 1}
          >
            ›
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Viewer;
